'use client'
import React, { useEffect, useRef, useState } from 'react';
import { isEqual, cloneDeep, get, set } from 'lodash';
import { useChat } from '@/app/lib/context/ChatContext';
import { RxCrossCircled } from "react-icons/rx";
import { useTagEditor } from '@/app/lib/context/TagEditorContext';

interface Props {
    lifeStyleImagesData: any[];
    selectedDesignLifeStyleImageIdForEdit: any;
    onSave: (updatedData: any) => void;
    activeParticularTemplateId: any;
    handleOpenParticularSnapShotView: any;
}

const TagEditor: React.FC<Props> = ({
    lifeStyleImagesData,
    selectedDesignLifeStyleImageIdForEdit,
    onSave,
    activeParticularTemplateId,
    handleOpenParticularSnapShotView
}) => {
    const { editedAiGeneratedData, setEditedAiGeneratedData, originalAiGeneratedDataRef, handleChange, modifiedFields, setModifiedFields, setSelectedDesignLifeStyleImageDetailsForEdit } = useTagEditor()
    const [editMode, setEditMode] = useState(false);
    const { handleSetOpenChat } = useChat();
    useEffect(() => {
        if (lifeStyleImagesData?.length > 0 && selectedDesignLifeStyleImageIdForEdit) {
            const selectedImage = lifeStyleImagesData.find(
                (img) => img.designLifeStyleImageId === selectedDesignLifeStyleImageIdForEdit
            );
            if (selectedImage) {
                setSelectedDesignLifeStyleImageDetailsForEdit(selectedImage);
                const safeCopy = cloneDeep(selectedImage.aiGeneratedData || {});
                setEditedAiGeneratedData(safeCopy);
                originalAiGeneratedDataRef.current = cloneDeep(safeCopy);
                setModifiedFields({});
            }
        }
    }, [lifeStyleImagesData, selectedDesignLifeStyleImageIdForEdit]);



    const handleRevertField = (path: string) => {
        const originalValue = get(originalAiGeneratedDataRef.current, path);
        setEditedAiGeneratedData((prev: any) => {
            const updated = cloneDeep(prev);
            set(updated, path, originalValue);
            return updated;
        });
        setModifiedFields((prev: any) => ({ ...prev, [path]: false }));
    };

    const handleSaveField = (path: string) => {
        onSave(editedAiGeneratedData);
        setModifiedFields((prev: any) => ({ ...prev, [path]: false }));
        originalAiGeneratedDataRef.current = cloneDeep(editedAiGeneratedData);
    };

    const renderField = (
        label: string,
        path: string,
        type: 'text' | 'textarea' = 'text'
    ) => {
        const value = get(editedAiGeneratedData, path) || '';
        const isFieldEdited = modifiedFields[path];

        return (
            <div key={path}>
                <div className='flex justify-between'>
                    <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-1">
                        {label}
                        {(label === 'Comfort' || label === 'Quality of paint') && (
                            <span className="text-orange-500">✴️</span>
                        )}
                    </h3>
                    {isFieldEdited && (
                        <div className="flex gap-3 mt-2">
                            <button
                                className="text-[#0D908E] bg-transparent cursor-pointer"
                                onClick={() => {
                                    handleSaveField(path);
                                    handleSetOpenChat({ isOpen: false, from: '' });
                                    setEditMode(false)
                                }}
                            >
                                Looks Good
                            </button>
                            <button
                                className="text-[#0D908E] bg-transparent cursor-pointer"
                                onClick={() => handleRevertField(path)}
                            >
                                Revert
                            </button>
                        </div>
                    )}
                </div>

                {editMode ? (type !== 'textarea' ? <input
                    type="text"
                    className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                    value={value}
                    onChange={(e) => handleChange(path, e.target.value)}
                /> :
                    <textarea
                        className="w-full border rounded p-2 min-h-[100px]"
                        value={value}
                        onChange={(e) => handleChange(path, e.target.value)}
                    />
                ) : (
                    <p className="text-gray-700 whitespace-pre-line">{value}</p>
                )}
            </div>
        );
    };

    return (
        <div className="p-6 max-w-4xl w-full">
            <div className="flex justify-end mb-4 gap-5">
                <button
                    onClick={() => {
                        if (!editMode) {
                            handleSetOpenChat({ isOpen: true, from: 'tagEditor' });
                        } else {
                            handleSetOpenChat({ isOpen: false, from: '' });
                        }
                        setEditMode(!editMode)
                    }}
                    className="cursor-pointer"
                >
                    {editMode ? <svg width="39" height="34" viewBox="0 0 39 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.25" width="39" height="32" rx="6" fill="#FE6526" />
                        <path d="M31.3327 8.5L15.7493 24.0833L8.66602 17" stroke="#25282B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                        : <svg width="39" height="32" viewBox="0 0 39 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect opacity="0.25" width="39" height="32" rx="6" fill="#FE6526" />
                            <path d="M25 8.0003C25.2626 7.73766 25.5744 7.52932 25.9176 7.38718C26.2608 7.24503 26.6286 7.17188 27 7.17188C27.3714 7.17187 27.7392 7.24503 28.0824 7.38718C28.4256 7.52932 28.7374 7.73766 29 8.0003C29.2626 8.26295 29.471 8.57475 29.6131 8.91791C29.7553 9.26107 29.8284 9.62887 29.8284 10.0003C29.8284 10.3717 29.7553 10.7395 29.6131 11.0827C29.471 11.4259 29.2626 11.7377 29 12.0003L15.5 25.5003L10 27.0003L11.5 21.5003L25 8.0003Z" stroke="#25282B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>}

                </button>
                <button onClick={() => { handleOpenParticularSnapShotView(activeParticularTemplateId) }} className="cursor-pointer">
                    <RxCrossCircled size={24} />
                </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="sm:col-span-2">
                    {renderField('Display Name', 'displayName', "textarea")}
                </div>
                <div className="sm:col-span-2">
                    {renderField('Design Description', 'designDescription', 'textarea')}
                </div>
                {renderField('Style', 'style', "textarea")}
                {renderField('Room Size', 'roomSize', "textarea")}
                {renderField('Color', 'colorPalette', "textarea")}
                {renderField('Light', 'light', "textarea")}
                {renderField('Tags', 'tags', "textarea")}
                {renderField('Design Goals', 'features.designGoals', 'textarea')}
                {renderField('Style Goals', 'features.styleGoals', 'textarea')}
                {renderField('Budget Level Goals', 'features.budgetLevelGoals', 'textarea')}
                {renderField('Usage Options', 'features.usageOptions', 'textarea')}
            </div>
        </div>
    );
};

export default TagEditor;
